import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BookOpen, Briefcase, Award, Download as DownloadIcon, Settings as SettingsIcon } from "lucide-react";
import { LearnShell } from "@/components/layouts/LearnShell";
import { Seo } from "@/components/seo/Seo";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { RequireAuth } from "@/components/auth/RequireAuth";

/**
 * `/app` — authenticated learner home with quick access to the 5 main areas
 * (bootcamps, downloads/library, coaching, certificates, settings).
 *
 * Each tile links to a real, fully-featured page so this shell adds no
 * business logic. It replaces the previous `/app -> /learn/dashboard`
 * redirect with an actual landing surface.
 */
function AppDashboardInner() {
  const { t } = useTranslation();

  const { data: counts } = useQuery({
    queryKey: ["app-shell-counts"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return { bootcamps: 0, downloads: 0, certificates: 0, bookings: 0 };
      const userId = u.user.id;
      const [bc, dl, ce, bk] = await Promise.all([
        supabase.from("bootcamp_enrollments").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase.from("download_progress").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase.from("certificates").select("id", { count: "exact", head: true }).eq("user_id", userId).eq("revoked", false),
        supabase.from("bookings").select("id", { count: "exact", head: true }).eq("learner_id", userId).neq("status", "cancelled"),
      ]);
      return {
        bootcamps: bc.count ?? 0,
        downloads: dl.count ?? 0,
        certificates: ce.count ?? 0,
        bookings: bk.count ?? 0,
      };
    },
  });

  const tiles: Array<{
    key: string;
    to: string;
    label: string;
    sub: string;
    count?: number;
    icon: JSX.Element;
  }> = [
    {
      key: "bootcamps",
      to: "/learn",
      label: t("app.tiles.bootcamps.title", "My bootcamps"),
      sub: t("app.tiles.bootcamps.sub", "Continue learning"),
      count: counts?.bootcamps,
      icon: <BookOpen className="h-5 w-5" />,
    },
    {
      key: "library",
      to: "/library",
      label: t("app.tiles.library.title", "Downloads"),
      sub: t("app.tiles.library.sub", "Templates & playbooks you own"),
      count: counts?.downloads,
      icon: <DownloadIcon className="h-5 w-5" />,
    },
    {
      key: "coaching",
      to: "/services/coaching",
      label: t("app.tiles.coaching.title", "Coaching"),
      sub: t("app.tiles.coaching.sub", "Sessions & retainers"),
      count: counts?.bookings,
      icon: <Briefcase className="h-5 w-5" />,
    },
    {
      key: "certificates",
      to: "/certificates",
      label: t("app.tiles.certificates.title", "Certificates"),
      sub: t("app.tiles.certificates.sub", "Your earned credentials"),
      count: counts?.certificates,
      icon: <Award className="h-5 w-5" />,
    },
    {
      key: "settings",
      to: "/settings/profile",
      label: t("app.tiles.settings.title", "Settings"),
      sub: t("app.tiles.settings.sub", "Profile, billing & preferences"),
      icon: <SettingsIcon className="h-5 w-5" />,
    },
  ];

  return (
    <>
      <Seo title={t("app.seoTitle", "Your dashboard · Bootpreneurs")} description={t("app.seoDescription", "Quick access to your bootcamps, downloads, coaching, and certificates.")} />
      <LearnShell
        topBar={
          <div>
            <h1 className="text-2xl font-display font-bold text-[hsl(var(--learn-text))]">
              {t("app.title", "Welcome back")}
            </h1>
            <p className="mt-1 text-sm text-[hsl(var(--learn-text-muted))]">
              {t("app.subtitle", "Pick up where you left off.")}
            </p>
          </div>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map((tile) => (
            <Link key={tile.key} to={tile.to} className="group">
              <Card
                className="h-full p-5 transition-all hover:-translate-y-0.5"
                style={{
                  background: "hsl(var(--learn-surface))",
                  borderColor: "hsl(var(--learn-border))",
                }}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="grid h-10 w-10 place-items-center rounded-[10px]"
                    style={{
                      background: "hsl(var(--learn-primary) / 0.12)",
                      color: "hsl(var(--learn-primary-2))",
                    }}
                  >
                    {tile.icon}
                  </div>
                  {typeof tile.count === "number" && (
                    <span
                      className="rounded-full px-2.5 py-0.5 text-xs font-bold"
                      style={{
                        background: "hsl(var(--learn-surface-2))",
                        color: "hsl(var(--learn-text))",
                      }}
                    >
                      {tile.count}
                    </span>
                  )}
                </div>
                <div className="mt-4 text-base font-semibold text-[hsl(var(--learn-text))]">
                  {tile.label}
                </div>
                <p className="mt-1 text-sm text-[hsl(var(--learn-text-muted))]">{tile.sub}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--learn-primary-2))] opacity-0 transition-opacity group-hover:opacity-100">
                  {t("app.tiles.open", "Open")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </LearnShell>
    </>
  );
}

export default function AppRedirect() {
  return (
    <RequireAuth>
      <AppDashboardInner />
    </RequireAuth>
  );
}
